import { useNavigate } from 'react-router-dom';
import { ActionItem } from './types';
import { DEPT_DB_MAP, formatAmount, titleCase, BASE, authFetch } from '../../constants';
import ActionButton from '../../components/ui/ActionButton';

interface ActionDetailProps {
  selected: ActionItem;
  notes: string;
  acting: boolean;
  flagReview: boolean;
  onClose: () => void;
  onSetNotes: (n: string) => void;
  onSetFlagReview: (f: boolean) => void;
  onDoAction: (actionId: number, action: string, body?: any) => void;
  onShowTransfer: () => void;
  onShowEscalate: () => void;
  onFetchControlGap: (exceptionType: string) => void;
  showToast: (msg: string, ok: boolean) => void;
  roleLevel: string;
}

export default function ActionDetail({
  selected, notes, acting, flagReview,
  onClose, onSetNotes, onSetFlagReview, onDoAction,
  onShowTransfer, onShowEscalate, onFetchControlGap, showToast, roleLevel,
}: ActionDetailProps) {
  const navigate = useNavigate();

  return (
    <div style={{
      background: 'var(--bg-card)', borderRadius: 8,
      border: '1px solid var(--border-subtle)', padding: 20, position: 'sticky', top: 80,
      maxHeight: 'calc(100vh - 120px)', overflowY: 'auto',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
          {selected.exception?.ref} Review
        </h3>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 16,
        }}>X</button>
      </div>

      {/* Exception info */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{selected.exception?.title}</div>
        {selected.exception?.description && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 8 }}>
            {selected.exception.description.substring(0, 300)}
            {(selected.exception.description.length || 0) > 300 ? '...' : ''}
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11, color: 'var(--text-tertiary)', alignItems: 'center' }}>
          <span>Type: <span style={{ color: 'var(--navy-800)' }}>{titleCase(selected.exception?.type)}</span>
            <ActionButton
              label="Why?"
              onClick={(e) => { e.stopPropagation(); if (selected.exception?.type) onFetchControlGap(selected.exception.type); }}
              variant="ghost"
              size="sm"
              title="View control gap analysis"
            />
          </span>
          <span>Amount: <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{formatAmount(selected.exception?.amount || 0)}</span></span>
          <span>Entity: <span style={{ color: '#67e8f9' }}>{selected.exception?.entity}</span></span>
          {selected.exception?.vendor_name && <span>{selected.counterparty_label || 'Vendor'}: {selected.exception.vendor_name}</span>}
        </div>
      </div>

      {/* Parent exception status (child actions) */}
      {selected.exception && (selected.exception.action_count || 0) > 1 && (
        <div style={{
          padding: 10, marginBottom: 16, borderRadius: 6,
          background: 'rgba(6,182,212,0.06)', border: '1px solid var(--accent-border)',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#67e8f9', marginBottom: 4 }}>PARENT EXCEPTION</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {selected.exception.action_count} actions: {selected.exception.actions_closed} closed, {(selected.exception.action_count || 0) - (selected.exception.actions_closed || 0)} pending
          </div>
          <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: 'rgba(0,0,0,0.06)' }}>
            <div style={{
              height: '100%', borderRadius: 2, background: 'var(--positive)',
              width: `${((selected.exception.actions_closed || 0) / Math.max(selected.exception.action_count || 1, 1)) * 100}%`,
              transition: 'width 0.3s',
            }} />
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>
            Status: <span style={{ color: 'var(--warning)' }}>{selected.exception.resolution_status}</span>
          </div>
        </div>
      )}

      {/* AI Resolution / Diagnosis */}
      {(selected.ai_resolution || selected.ai_diagnosis) ? (
        <div style={{
          padding: 12, background: 'rgba(111,66,193,0.06)', borderRadius: 6,
          border: '1px solid #f5f3ff', marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-800)' }}>
              {selected.ai_resolution ? 'AI RECOMMENDATION' : 'AI DIAGNOSIS'}
            </span>
            {selected.ai_diagnosis?.confidence != null && (() => {
              const pct = selected.ai_diagnosis!.confidence! * 100;
              const color = pct >= 90 ? 'var(--positive)' : pct >= 70 ? 'var(--warning)' : '#ef4444';
              const bg = pct >= 90 ? 'rgba(34,197,94,0.12)' : pct >= 70 ? 'rgba(251,191,36,0.12)' : '#fef2f2';
              return (
                <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, color, background: bg }}>
                  {pct.toFixed(0)}%
                </span>
              );
            })()}
          </div>
          {selected.ai_resolution?.recommendation && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 6 }}>{selected.ai_resolution.recommendation}</div>
          )}
          {selected.sil_score != null && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
              SIL Score: <span style={{ color: 'var(--positive)', fontWeight: 600 }}>{(selected.sil_score * 100).toFixed(1)}%</span>
            </div>
          )}
          {selected.ai_diagnosis?.reasoning && (
            <div style={{ fontSize: 10, color: 'var(--text-tertiary)', lineHeight: 1.4, marginBottom: 6 }}>{selected.ai_diagnosis.reasoning}</div>
          )}
          {(selected.standards_cited || []).length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
              {(selected.standards_cited || []).map((s: string, i: number) => (
                <span key={i} style={{ padding: '2px 8px', borderRadius: 10, fontSize: 9, fontWeight: 600, background: 'rgba(59,130,246,0.12)', color: 'var(--navy-700)' }}>{s}</span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: 12, background: 'rgba(0,0,0,0.02)', borderRadius: 6, border: '1px solid var(--border-subtle)', marginBottom: 16, textAlign: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>AI analysis not yet available</span>
        </div>
      )}

      {/* Proposed JE */}
      {selected.ai_diagnosis?.proposed_je && (() => {
        const lines = selected.ai_diagnosis!.proposed_je?.lines || selected.ai_diagnosis!.proposed_je?.entries ||
          (Array.isArray(selected.ai_diagnosis!.proposed_je) ? selected.ai_diagnosis!.proposed_je : []);
        if (!lines || lines.length === 0) return null;
        return (
          <div style={{ padding: 12, background: 'var(--accent-subtle)', borderRadius: 6, border: '1px solid rgba(6,182,212,0.12)', marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#67e8f9', marginBottom: 8 }}>PROPOSED JOURNAL ENTRY</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <th style={{ textAlign: 'left', padding: '4px 6px', color: 'var(--text-tertiary)', fontSize: 9, fontWeight: 600 }}>Account</th>
                  <th style={{ textAlign: 'right', padding: '4px 6px', color: 'var(--text-tertiary)', fontSize: 9, fontWeight: 600 }}>Debit</th>
                  <th style={{ textAlign: 'right', padding: '4px 6px', color: 'var(--text-tertiary)', fontSize: 9, fontWeight: 600 }}>Credit</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line: any, i: number) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                    <td style={{ padding: '4px 6px', color: 'var(--text-secondary)' }}>{line.account_code || line.account || '-'}</td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: line.debit ? '#fff' : 'var(--text-tertiary)' }}>
                      {line.debit ? formatAmount(line.debit) : '-'}
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: line.credit ? '#fff' : 'var(--text-tertiary)' }}>
                      {line.credit ? formatAmount(line.credit) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Transfer History Timeline */}
      {selected.transfers && selected.transfers.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', marginBottom: 8, textTransform: 'uppercase' }}>
            Transfer History ({selected.transfers.length})
          </div>
          <div style={{ background: 'rgba(245,158,11,0.04)', borderRadius: 6, paddingLeft: 12, marginLeft: 4 }}>
            {selected.transfers.map((t, i) => (
              <div key={t.id} style={{ marginBottom: i < selected.transfers!.length - 1 ? 10 : 0, position: 'relative' }}>
                <div style={{
                  position: 'absolute', left: -17, top: 4, width: 8, height: 8,
                  borderRadius: '50%',
                  background: t.transfer_type === 'ESCALATION' ? '#ef4444'
                    : t.transfer_type === 'TRANSFER' ? '#f59e0b'
                    : 'var(--positive)',
                }} />
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 2 }}>
                  {new Date(t.transferred_at).toLocaleString()} — <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{t.transfer_type}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--warning)' }}>{(DEPT_DB_MAP[t.from_department] || t.from_department)}</span>
                  {' / '}{titleCase(t.from_role)} ({t.from_level})
                  {' -> '}
                  <span style={{ color: '#67e8f9' }}>{(DEPT_DB_MAP[t.to_department] || t.to_department)}</span>
                  {' / '}{titleCase(t.to_role)} ({t.to_level})
                </div>
                {t.transfer_reason && t.transfer_reason !== 'Initial routing from S31 assignment engine' && (
                  <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2, fontStyle: 'italic' }}>
                    {t.transfer_reason}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Prior Findings */}
      {selected.prior_findings && selected.prior_findings.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-800)', marginBottom: 8, textTransform: 'uppercase' }}>Prior Findings</div>
          {selected.prior_findings.map((f, i) => (
            <div key={i} style={{
              padding: 8, marginBottom: 4, borderRadius: 4,
              background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.1)',
              fontSize: 11,
            }}>
              <span style={{ color: 'var(--navy-800)', fontWeight: 600 }}>{(DEPT_DB_MAP[f.department] || f.department)}</span>
              <span style={{ color: 'var(--text-tertiary)' }}> / {titleCase(f.owner)}</span>
              {f.finding && <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{f.finding}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Cascade Authority Badge (S38A) */}
      {selected.cascade_authority && (
        <div style={{
          padding: '8px 12px', marginBottom: 16, borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: selected.cascade_authority === 'REVIEW' ? 'rgba(59,130,246,0.06)' : 'rgba(34,197,94,0.06)',
          border: `1px solid ${selected.cascade_authority === 'REVIEW' ? 'rgba(59,130,246,0.2)' : 'rgba(34,197,94,0.2)'}`,
        }}>
          <div>
            <span style={{
              fontSize: 11, fontWeight: 700,
              color: selected.cascade_authority === 'REVIEW' ? 'var(--navy-600)' : 'var(--positive)',
            }}>
              {selected.cascade_authority === 'REVIEW' ? 'REVIEW' : 'APPROVE'} — L{selected.cascade_level || 2}
            </span>
            {selected.cascade_authority === 'REVIEW' && selected.approval_level_required && (
              <span style={{ fontSize: 10, color: 'var(--text-tertiary)', marginLeft: 8 }}>
                Approval requires L{selected.approval_level_required}
              </span>
            )}
          </div>
          {selected.flagged_by_reviewer && (
            <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, background: '#fef2f2', color: '#ef4444', fontWeight: 700 }}>
              FLAGGED
            </span>
          )}
        </div>
      )}

      {/* Review Chain Timeline (S38A) */}
      {selected.review_context?.review_chain && selected.review_context.review_chain.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-600)', marginBottom: 8, textTransform: 'uppercase' }}>
            Review Trail ({selected.review_context.review_chain.length} level{selected.review_context.review_chain.length !== 1 ? 's' : ''})
          </div>
          <div style={{ background: 'var(--accent-subtle)', borderRadius: 6, paddingLeft: 12, marginLeft: 4 }}>
            {selected.review_context.review_chain.map((review, i) => (
              <div key={i} style={{ marginBottom: i < selected.review_context!.review_chain.length - 1 ? 10 : 0, position: 'relative' }}>
                <div style={{
                  position: 'absolute', left: -17, top: 4, width: 8, height: 8, borderRadius: '50%',
                  background: review.flagged ? '#ef4444' : 'var(--navy-600)',
                }} />
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 2 }}>
                  <span style={{ fontWeight: 700, color: 'var(--navy-600)' }}>{review.level}</span>
                  {' — '}{review.reviewer}
                  {review.flagged && <span style={{ marginLeft: 6, fontSize: 9, color: '#ef4444', fontWeight: 700 }}>FLAGGED</span>}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                  {new Date(review.timestamp).toLocaleString()} — {titleCase(DEPT_DB_MAP[review.department] || review.department)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.4 }}>{review.notes}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes input */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
          {selected.cascade_authority === 'REVIEW' ? 'Review Notes (required)' : 'Review Notes (required for approve/modify/reject)'}
        </label>
        <textarea
          value={notes}
          onChange={e => onSetNotes(e.target.value)}
          placeholder="Enter your review notes..."
          style={{
            width: '100%', minHeight: 80, padding: 10, borderRadius: 6,
            background: 'var(--bg-base)', border: '1px solid var(--border-subtle)',
            color: 'var(--text-primary)', fontSize: 12, resize: 'vertical', fontFamily: 'inherit',
          }}
        />
      </div>

      {/* Action buttons -- cascade-aware (S38A) */}
      {selected.cascade_authority === 'REVIEW' ? (
        <>
          {/* Flag issue checkbox */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 11, color: '#ef4444', cursor: 'pointer' }}>
            <input type="checkbox" checked={flagReview} onChange={e => onSetFlagReview(e.target.checked)} />
            Flag issue for higher-level attention
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <ActionButton
              label="Complete Review & Escalate"
              onClick={() => onDoAction(selected.id, 'complete-review', { notes, flagged: flagReview })}
              variant="primary"
              disabled={acting || !notes.trim()}
            />
            <ActionButton
              label="Reject"
              onClick={() => onDoAction(selected.id, 'reject', { rejection_reason: notes, rejected_by: 'reviewer' })}
              variant="danger"
              disabled={acting || !notes.trim()}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <ActionButton
              label="Transfer"
              onClick={onShowTransfer}
              variant="secondary"
              disabled={acting}
            />
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <ActionButton
              label="Approve"
              onClick={() => onDoAction(selected.id, 'approve', { approved_by: 'reviewer', role_level: roleLevel })}
              variant="success"
              disabled={acting || !notes.trim()}
            />
            <ActionButton
              label="Modify"
              onClick={() => onDoAction(selected.id, 'modify', { modified_je: {}, modification_reason: notes, modified_by: 'reviewer', role_level: roleLevel })}
              variant="secondary"
              disabled={acting || !notes.trim()}
            />
            <ActionButton
              label="Reject"
              onClick={() => onDoAction(selected.id, 'reject', { rejection_reason: notes, rejected_by: 'reviewer' })}
              variant="danger"
              disabled={acting || !notes.trim()}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <ActionButton
              label="Transfer"
              onClick={onShowTransfer}
              variant="secondary"
              disabled={acting}
            />
            <ActionButton
              label="Escalate"
              onClick={onShowEscalate}
              variant="secondary"
              disabled={acting}
            />
          </div>
        </>
      )}

      {/* JE Package Download (S38A) */}
      {selected.je_id && (
        <div style={{
          padding: '10px 12px', marginTop: 12, borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'rgba(34,197,94,0.06)', border: '1px solid #f0fdf4',
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--positive)' }}>JOURNAL ENTRY POSTED</div>
            <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
              JE #{selected.je_id} — {selected.je_status || 'POSTED'}
            </div>
          </div>
          <ActionButton
            label="Download Package"
            onClick={async () => {
              try {
                const res = await authFetch(`${BASE}/api/financial/export/je-package/${selected.je_id}`);
                if (res.ok) {
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `JE_Package_${selected.je_id}.zip`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } else {
                  showToast('Download failed', false);
                }
              } catch { showToast('Download failed', false); }
            }}
            variant="success"
            size="sm"
          />
        </div>
      )}

      {/* Link to full exception */}
      <div style={{ marginTop: 12, textAlign: 'center' }}>
        <button
          onClick={() => navigate(`/tasks?highlight=${selected.exception_id}`)}
          style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}
        >View full exception in Task Center</button>
      </div>
    </div>
  );
}
